import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Upload, 
  FileText, 
  Database, 
  Trash2, 
  AlertTriangle,
  CheckCircle,
  XCircle,
  RefreshCw,
  Download
} from 'lucide-react';

// Use environment variable for API URL
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://sledovanie-aut-api.onrender.com';

const AdminPanel = ({ onLogout }) => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadResults, setUploadResults] = useState([]);
  const [uploadBatches, setUploadBatches] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [adminStats, setAdminStats] = useState(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [confirmationStep, setConfirmationStep] = useState(0);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    loadUploadBatches();
    loadAdminStats();
  }, []);

  const loadAdminStats = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/admin/stats`);
      setAdminStats(response.data);
    } catch (error) {
      console.error('Chyba pri načítaní admin štatistík:', error);
    }
  };

  const loadUploadBatches = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/batches`);
      setUploadBatches(response.data);
    } catch (error) {
      console.error('Chyba pri načítaní zoznamu nahraní:', error);
    }
  };

  const handleFileChange = (event) => {
    const files = Array.from(event.target.files);
    setSelectedFiles(files);
    setUploadResults([]);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files).filter(file => file.name.endsWith('.csv'));
    setSelectedFiles(files);
    setUploadResults([]);
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      alert('Vyberte prosím aspoň jeden CSV súbor');
      return;
    }

    setUploading(true);
    setUploadResults([]);

    try {
      const formData = new FormData();
      selectedFiles.forEach(file => {
        formData.append('files', file);
      });

      const response = await axios.post(`${API_BASE_URL}/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setUploadResults(response.data.results);
      setSelectedFiles([]);
      document.getElementById('file-input').value = '';

      // Aktualizácia zoznamu nahraní
      await loadUploadBatches();

    } catch (error) {
      console.error('Chyba pri nahrávaní:', error);
      setUploadResults([{
        filename: 'Chyba',
        status: 'error',
        message: error.response?.data?.detail || 'Neočakávaná chyba pri nahrávaní'
      }]);
    } finally {
      setUploading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('sk-SK');
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleDeleteDatabase = () => {
    setShowDeleteConfirmation(true);
    setConfirmationStep(0);
    setDeleteConfirmText('');
  };

  const proceedToNextStep = () => {
    setConfirmationStep(confirmationStep + 1);
  };

  const confirmDeleteDatabase = async () => {
    if (deleteConfirmText !== 'VYMAZAŤ') {
      alert('Pre potvrdenie musíte napísať "VYMAZAŤ"');
      return;
    }

    setDeleting(true);
    try {
      const response = await axios.delete(`${API_BASE_URL}/admin/database`);
      
      if (response.data.status === 'success') {
        alert('Databáza bola úspešne vymazaná!');
        setShowDeleteConfirmation(false);
        setUploadBatches([]);
        setUploadResults([]);
        setSelectedFiles([]);
        loadAdminStats(); // Aktualizácia štatistík
      }
    } catch (error) {
      alert(`Chyba pri mazaní databázy: ${error.response?.data?.detail || error.message}`);
    } finally {
      setDeleting(false);
    }
  };

  const cancelDelete = () => {
    setShowDeleteConfirmation(false);
    setConfirmationStep(0);
    setDeleteConfirmText('');
  };

  return (
    <div className="space-y-6">

      {/* Upload sekcia */}
      <div className="card">
        <div className="card-header">
          <div className="card-title">
            <Upload size={20} />
            Nahrávanie CSV súborov
          </div>
          <div className="card-description">
            Nahrajte CSV súbory s údajmi o vozidlách
          </div>
        </div>
        <div className="card-content">
          <div 
            className={`upload-dropzone ${dragOver ? 'drag-over' : ''}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <div className="dropzone-content">
              <Upload size={48} className="dropzone-icon" />
              <div className="dropzone-text">
                <p className="font-semibold">Presuňte CSV súbory sem</p>
                <p className="text-gray-500">alebo kliknite na výber súborov</p>
              </div>
              <input
                id="file-input"
                type="file"
                multiple
                accept=".csv"
                onChange={handleFileChange}
                className="file-input"
              />
              <label htmlFor="file-input" className="btn btn-primary">
                Vybrať súbory
              </label>
            </div>
          </div>

          {selectedFiles.length > 0 && (
            <div className="selected-files">
              <h4 className="font-semibold mb-4">Vybrané súbory:</h4>
              <div className="file-list">
                {selectedFiles.map((file, index) => (
                  <div key={index} className="file-item">
                    <FileText size={20} className="file-icon" />
                    <span className="file-name">{file.name}</span>
                    <span className="file-size">{formatFileSize(file.size)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="upload-actions">
            <button
              className="btn btn-primary btn-lg"
              onClick={handleUpload}
              disabled={uploading || selectedFiles.length === 0}
            >
              {uploading ? (
                <>
                  <RefreshCw size={16} className="animate-spin" />
                  Nahráva sa...
                </>
              ) : (
                <>
                  <Upload size={16} />
                  Nahrať súbory
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Výsledky nahrávania */}
      {uploadResults.length > 0 && (
        <div className="card">
          <div className="card-header">
            <div className="card-title">Výsledky nahrávania</div>
          </div>
          <div className="card-content">
            <div className="space-y-4">
              {uploadResults.map((result, index) => (
                <div
                  key={index}
                  className={`result-item ${result.status === 'success' ? 'result-success' : 'result-error'}`}
                >
                  {result.status === 'success' ? (
                    <CheckCircle size={20} className="result-icon text-green-600" />
                  ) : (
                    <XCircle size={20} className="result-icon text-red-600" />
                  )}
                  <div className="result-content">
                    <div className="font-semibold">{result.filename}</div>
                    <div className="result-message text-sm text-gray-600">
                      {result.status === 'success' 
                        ? `Úspešne nahraných ${result.records_imported} záznamov`
                        : `Chyba: ${result.message}`
                      }
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* História nahrávania */}
      <div className="card">
        <div className="card-header">
          <div className="card-title">
            <FileText size={20} />
            História nahrávania
          </div>
          <div className="card-description">
            Posledných 10 nahraných súborov
          </div>
        </div>
        <div className="card-content">
          {uploadBatches.length > 0 ? (
            <div className="space-y-4">
              {uploadBatches.slice(0, 10).map((batch, index) => (
                <div key={index} className="history-item">
                  <Database size={20} className="history-icon text-blue-600" />
                  <div className="history-content">
                    <div className="history-filename font-semibold">{batch.filename}</div>
                    <div className="history-details text-sm text-gray-600">
                      {batch.records_count} záznamov • {formatDate(batch.upload_timestamp)}
                    </div>
                  </div>
                  <div className="history-status">
                    <span className="status-badge success">
                      <CheckCircle size={14} />
                      Úspešné
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-state">
              <FileText className="empty-icon" size={48} />
              <div className="empty-title">Žiadne súbory</div>
              <div className="empty-description">Zatiaľ neboli nahrané žiadne súbory</div>
            </div>
          )}
        </div>
      </div>

      {/* Informácie o filtrovaní */}
      <div className="card">
        <div className="card-header">
          <div className="card-title">
            <AlertTriangle size={20} />
            Informácie o filtrovaní údajov
          </div>
        </div>
        <div className="card-content">
          <div className="filter-info">
            <ul className="space-y-2">
              <li className="flex items-start gap-2">
                <span className="text-blue-600">•</span>
                <span>Automaticky sa ignorujú záznamy s <code className="bg-gray-100 px-1 rounded">entry_type</code> obsahujúcim "red_roi_entry"</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-600">•</span>
                <span>Automaticky sa ignorujú záznamy s nevalidným <code className="bg-gray-100 px-1 rounded">vehicle_id</code> (nie integer)</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-600">•</span>
                <span>Stĺpec <code className="bg-gray-100 px-1 rounded">vehicle_id</code> sa neukladá do databázy</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Správa databázy */}
      <div className="card">
        <div className="card-header">
          <div className="card-title">
            <Database size={20} />
            Správa databázy
          </div>
          <div className="card-description">
            Prehľad a správa databázových údajov
          </div>
        </div>
        <div className="card-content">
          {adminStats && (
            <div className="database-stats mb-6">
              <div className="stats-grid">
                <div className="stat-card">
                  <div className="stat-header">
                    <div className="stat-title">Celkový počet záznamov</div>
                    <Database className="stat-icon" size={24} />
                  </div>
                  <div className="stat-value">{adminStats.total_vehicle_records.toLocaleString()}</div>
                </div>
                
                <div className="stat-card">
                  <div className="stat-header">
                    <div className="stat-title">Nahraných súborov</div>
                    <FileText className="stat-icon" size={24} />
                  </div>
                  <div className="stat-value">{adminStats.total_upload_batches}</div>
                </div>
                
                <div className="stat-card">
                  <div className="stat-header">
                    <div className="stat-title">Veľkosť databázy</div>
                    <Download className="stat-icon" size={24} />
                  </div>
                  <div className="stat-value">{adminStats.database_size_mb} MB</div>
                </div>
              </div>
              
              {adminStats.oldest_record && (
                <div className="database-info bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-blue-800">
                    <strong>Časové rozpätie údajov:</strong> {formatDate(adminStats.oldest_record)} - {formatDate(adminStats.newest_record)}
                  </p>
                </div>
              )}
            </div>
          )}

          <div className="danger-zone bg-red-50 border border-red-200 rounded-lg p-6">
            <div className="flex items-center gap-3 mb-4">
              <AlertTriangle size={24} className="text-red-600" />
              <h4 className="text-lg font-semibold text-red-800">Nebezpečná zóna</h4>
            </div>
            <p className="text-red-700 mb-4">
              Nasledujúca akcia vymaže všetky údaje z databázy. Táto akcia je <strong>nevratná</strong>!
            </p>
            <button 
              className="btn btn-danger"
              onClick={handleDeleteDatabase}
              disabled={deleting}
            >
              <Trash2 size={16} />
              {deleting ? 'Mazanie...' : 'Vymazať celú databázu'}
            </button>
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      {showDeleteConfirmation && (
        <div className="modal-overlay">
          <div className="confirmation-modal">
            {confirmationStep === 0 && (
              <>
                <h3>⚠️ Vymazanie databázy</h3>
                <p>Naozaj chcete vymazať <strong>celú databázu</strong>?</p>
                {adminStats && (
                  <div className="warning-stats">
                    <p>Stratíte:</p>
                    <ul>
                      <li><strong>{adminStats.total_vehicle_records.toLocaleString()}</strong> záznamov vozidiel</li>
                      <li><strong>{adminStats.total_upload_batches}</strong> nahraných súborov</li>
                      <li>Všetky historické údaje</li>
                    </ul>
                  </div>
                )}
                <div className="modal-actions">
                  <button className="btn btn-outline" onClick={cancelDelete}>
                    Zrušiť
                  </button>
                  <button className="btn btn-danger" onClick={proceedToNextStep}>
                    Pokračovať
                  </button>
                </div>
              </>
            )}

            {confirmationStep === 1 && (
              <>
                <h3>🚨 Posledné potvrdenie</h3>
                <p>Pre potvrdenie vymazania napíšte: <strong>VYMAZAŤ</strong></p>
                <input
                  type="text"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder="Napíšte: VYMAZAŤ"
                  className="confirmation-input"
                  autoFocus
                />
                <div className="modal-actions">
                  <button className="btn btn-outline" onClick={cancelDelete}>
                    Zrušiť
                  </button>
                  <button 
                    className="btn btn-danger" 
                    onClick={confirmDeleteDatabase}
                    disabled={deleting || deleteConfirmText !== 'VYMAZAŤ'}
                  >
                    {deleting ? 'Mazanie...' : 'Vymazať databázu'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPanel;